// src/App.js
import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  Navigate,
} from "react-router-dom";
import Header from "components/Header";
import Footer from "components/Footer";
import { useUser } from "./context/UserContext";
import finalConfig from "./configs/config";

import styles from "page.module.css";

// Import all page components
import Dashboard from "pages/Home";
import Home from "pages/page";
import Unauthorized from "pages/Unauthorized";
import NotFound from "pages/NotFound";

import KeywordResearchKeyword from "pages/seo/keyword_research/single_keyword";
import KeywordResearchURL from "pages/seo/keyword_research/url";
import TitleSingleKeyword from "pages/seo/title/single_keyword";
import DomainRanking from "pages/seo/ranking/domain_ranking";
import PageRanking from "pages/seo/ranking/page_ranking";
import SingleKeywordRanking from "pages/seo/ranking/single_keyword_ranking";
import MetaDescriptionSingleKeyword from "pages/seo/meta_description/single_keyword";
import MetaDescriptionURL from "pages/seo/meta_description/url";
import ImageOptimisationKeyword from "pages/seo/image/keyword_optimisation";
import TitleURL from "pages/seo/title/url";

import ArticleHeadlinesKeyword from "pages/content_editor/article_headlines/keyword";
import ArticleHeadlinesURL from "pages/content_editor/article_headlines/url";

import WriterKeywordLevelContentOutline from "pages/content_editor/writer/keyword";
import WriterPageLevelContentOutline from "pages/content_editor/writer/url";
import WriterCopyAndPasteKeyword from "pages/content_editor/writer/content";

import KeywordSingle from "pages/content_editor/keyword_discovery/single_keyword";
import KeywordURL from "pages/content_editor/keyword_discovery/url";

import WriterAiOverviewKeyword from "pages/content_editor/ai_overview/keyword";
import WriterAiOverviewUrl from "pages/content_editor/ai_overview/url";
import WriterAiOverviewContent from "pages/content_editor/ai_overview/content";

import KeywordSummary from "pages/content_editor/content_journey/keyword";
import URLSummary from "pages/content_editor/content_journey/url";

import SocialTags from "pages/social/meta_insta/tags";
import SocialContentCreation from "pages/social/meta_insta/content_creation";
import Loader from "components/Loader";
import SignIn from "pages/signIn/page";
import SideBar from "components/SideBar";
import Plans from "pages/plans";
import Transactions from "pages/plans/transactions";
import BillingActivity from "pages/billing/activity";
import PaymentCancel from "pages/payment/PaymentCancel";
import PaymentSuccess from "pages/payment/PaymentSuccess";
import PaymentSuccessFinal from "pages/payment/PaymentSuccessFinal";
import PaymentCancelled from "pages/payment/PaymentCancelled";
import Privacy from "pages/privacy";
import Terms from "pages/terms";

const definedRoutes = [
  "/",
  "/home",

  "/seo/keyword_research/keyword",
  "/seo/keyword_research/url",
  "/seo/meta_description/keyword",
  "/seo/meta_description/url",
  "/seo/title/keyword",
  "/seo/title/url",
  "/seo/ranking/domain_ranking",
  "/seo/ranking/keyword",
  "/seo/ranking/page_ranking",
  "/seo/image/keyword_optimisation",
  "/seo/image/alt_tag_optimisation",

  "/content_editor/headlines/keyword",
  "/content_editor/headlines/url",
  "/content_editor/writer/url",
  "/content_editor/writer/keyword",
  "/content_editor/writer/content",
  "/content_editor/keyword_discovery/keyword",
  "/content_editor/keyword_discovery/url",
  "/content_editor/ai_overview/keyword",
  "/content_editor/ai_overview/url",
  "/content_editor/ai_overview/content",
  "/content_editor/import_content/content",
  "/content_editor/content_journey/keyword",
  "/content_editor/content_journey/url",

  "/social/meta_insta/tags",
  "/social/meta_insta/content_creation",

  "/unauthorized",
  "/signIn",
  "/plans",
  "/plans/transactions",
  "/billing/activity",
  "/payment-cancelled",
  "/payment-success",
  "/payment/cancel",
  "/payment/success",
  "/privacy",
  "/terms",
];

const Layout = ({ children }) => {
  const location = useLocation();
  const { isAuthenticated } = useUser();
  const isHomePage = location.pathname === "/";
  const isUnauthorized = location.pathname === "/unauthorized";
  const isSignIn = location.pathname === "/signIn";
  const isPlansPage = location.pathname === "/plans";
  const isDefinedPath = definedRoutes.includes(location.pathname);

  // Show header and sidebar for authenticated pages (including plans when authenticated)
  const showHeaderAndSidebar =
    !isHomePage &&
    !isUnauthorized &&
    !isSignIn &&
    isDefinedPath &&
    (isAuthenticated || (!isPlansPage && !isHomePage));

  // Show only header for landing page and plans page when not authenticated
  const showHeaderOnly = isHomePage || (isPlansPage && !isAuthenticated);

  return (
    <>
      {(showHeaderAndSidebar || showHeaderOnly) && <Header />}
      {showHeaderAndSidebar && <SideBar />}
      <main className={styles.main}>{children}</main>
      <Footer />
    </>
  );
};

const ProtectedRoute = ({ element, requiredRole = null }) => {
  const { isAuthenticated, loading, derivedState, logout } = useUser();
  const location = useLocation();

  // Define a whitelist of roles that are allowed access
  const allowedRoles = finalConfig.APP_ALLOWED_ROLES;
  if (loading) {
    return <Loader />;
  }

  if (!isAuthenticated) {
    // Store redirect URL in sessionStorage for WSO2/OAuth compatibility
    const redirectUrl = location.pathname + location.search;
    sessionStorage.setItem('wso2_redirect_url', redirectUrl);
    console.log("🔍 DEBUG - Stored redirect URL:", redirectUrl);
    return <Navigate to="/signIn" replace />;
  }

  const roles = derivedState?.authenticateResponse?.roles;

  // If roles are undefined or null, force logout and redirect to sign-in
  if (!roles) {
    logout();
    return <Navigate to="/unauthorized" replace />;
  }
  // Normalize roles to an array if it's a single string
  const normalizedRoles = Array.isArray(roles) ? roles : [roles];

  // Check access based on allowedRoles and requiredRole
  const hasAccess =
    normalizedRoles.some((role) => allowedRoles.includes(role)) &&
    (requiredRole ? normalizedRoles.includes(requiredRole) : true);
  
  if (!hasAccess) {
    return <Navigate to="/unauthorized" replace />;
  }

  return element;
};

const App = () => {
  return (
    <Router>
      <Suspense fallback={<div>Loading...</div>}>
        <Layout>
          <Routes>
            {/* Main Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/signIn" element={<SignIn />} />
            <Route
              path="/home"
              element={<ProtectedRoute element={<Dashboard />} />}
            />

            {/* SEO Routes */}
            {/* -->SEO KEYWORD RESEARCH */}
            <Route
              path="/seo/keyword_research/keyword"
              element={<ProtectedRoute element={<KeywordResearchKeyword />} />}
            />
            <Route
              path="/seo/keyword_research/url"
              element={<ProtectedRoute element={<KeywordResearchURL />} />}
            />

            {/* -->SEO TITLE */}
            <Route
              path="/seo/title/keyword"
              element={<ProtectedRoute element={<TitleSingleKeyword />} />}
            />
            <Route
              path="/seo/title/url"
              element={<ProtectedRoute element={<TitleURL />} />}
            />
            {/* -->SEO META DESCRIPTION */}
            <Route
              path="/seo/meta_description/keyword"
              element={
                <ProtectedRoute element={<MetaDescriptionSingleKeyword />} />
              }
            />
            <Route
              path="/seo/meta_description/url"
              element={<ProtectedRoute element={<MetaDescriptionURL />} />}
            />

            {/* -->SEO RANKING */}
            <Route
              path="/seo/ranking/keyword"
              element={<ProtectedRoute element={<SingleKeywordRanking />} />}
            />
            <Route
              path="/seo/ranking/domain_ranking"
              element={<ProtectedRoute element={<DomainRanking />} />}
            />
            <Route
              path="/seo/ranking/page_ranking"
              element={<ProtectedRoute element={<PageRanking />} />}
            />
            {/* -->SEO IMAGE */}
            <Route
              path="/seo/image/keyword_optimisation"
              element={
                <ProtectedRoute element={<ImageOptimisationKeyword />} />
              }
            />

            {/* Content Editor Routes */}
            {/* -->HEADLINE GENERATION */}
            {/* -->KEYWORD-Based */}
            <Route
              path="/content_editor/headlines/keyword"
              element={<ProtectedRoute element={<ArticleHeadlinesKeyword />} />}
            />
            {/* -->URL-Based */}
            <Route
              path="/content_editor/headlines/url"
              element={<ProtectedRoute element={<ArticleHeadlinesURL />} />}
            />

            {/* -->CONTENT OPTIMISER */}
            {/* -->URL-Based Optimisation */}
            <Route
              path="/content_editor/writer/url"
              element={
                <ProtectedRoute element={<WriterPageLevelContentOutline />} />
              }
            />
            {/* -->KEYWORD-Based Optimisation */}
            <Route
              path="/content_editor/writer/keyword"
              element={
                <ProtectedRoute
                  element={<WriterKeywordLevelContentOutline />}
                />
              }
            />
            {/* -->CONTENT-Based Optimisation */}
            <Route
              path="/content_editor/writer/content"
              element={
                <ProtectedRoute element={<WriterCopyAndPasteKeyword />} />
              }
            />
            
            {/* -->AI OVERVIEW */}
            <Route
              path="/content_editor/ai_overview/keyword"
              element={
                <ProtectedRoute element={<WriterAiOverviewKeyword />} />
              }
            />
            <Route
              path="/content_editor/ai_overview/url"
              element={
                <ProtectedRoute element={<WriterAiOverviewUrl />} />
              }
            />
            <Route
              path="/content_editor/ai_overview/content"
              element={
                <ProtectedRoute element={<WriterAiOverviewContent />} />
              }
            />

            {/* -->KEYWORD DISCOVERY */}
            <Route
              path="/content_editor/keyword_discovery/keyword"
              element={<ProtectedRoute element={<KeywordSingle />} />}
            />
            <Route
              path="/content_editor/keyword_discovery/url"
              element={<ProtectedRoute element={<KeywordURL />} />}
            />
            {/* -->IMPORT CONTENT */}
            <Route
              path="/content_editor/import_content/content"
              element={<ProtectedRoute element={<WriterCopyAndPasteKeyword/>} />}
            />
            
            {/* -->SUMMARY */}
            <Route
              path="/content_editor/content_journey/keyword"
              element={<ProtectedRoute element={<KeywordSummary />} />}
            />
            <Route
              path="/content_editor/content_journey/url"
              element={<ProtectedRoute element={<URLSummary />} />}
            />

            {/* Social Routes */}
            {/* -->SOCIAL META + INSTA */}
            <Route
              path="/social/meta_insta/tags"
              element={<ProtectedRoute element={<SocialTags />} />}
            />
            <Route
              path="/social/meta_insta/content_creation"
              element={<ProtectedRoute element={<SocialContentCreation />} />}
            />

            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Plans Routes */}
            <Route path="/plans" element={<Plans />} />
            <Route
              path="/plans/transactions"
              element={<ProtectedRoute element={<Transactions />} />}
            />

            {/* Billing Routes */}
            <Route
              path="/billing/activity"
              element={<ProtectedRoute element={<BillingActivity />} />}
            />

            {/* Payment Routes */}
            <Route
              path="/payment/cancel"
              element={<ProtectedRoute element={<PaymentCancel />} />}
            />
            <Route
              path="/payment/success"
              element={<ProtectedRoute element={<PaymentSuccess />} />}
            />
            <Route
              path="/payment-success"
              element={<ProtectedRoute element={<PaymentSuccessFinal />} />}
            />
            <Route
              path="/payment-cancelled"
              element={<ProtectedRoute element={<PaymentCancelled />} />}
            />

            {/* Legal Routes */}
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />

            {/* Not Found Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Layout>
      </Suspense>
    </Router>
  );
};

export default App;
